import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Search, ExternalLink } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cepres-edu/artifacts/tldo3ilo_ciu-logo.png';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navLinks = [
    { 
      name: 'ABOUT CIU', 
      path: '/about',
      dropdown: [
        { name: 'Our Story', path: '/about' },
        { name: 'Why Choose CIU', path: '/why-ciu' },
        { name: 'Student Stories', path: '/stories' },
        { name: 'Accreditation', path: '/accreditation' },
      ]
    },
    { 
      name: 'PROGRAMS', 
      path: '/programs',
      dropdown: [
        { name: 'Degree Programs', path: '/programs' },
        { name: 'Vocational Programs', path: '/vocational' },
        { name: 'Campus Services', path: '/services' },
      ]
    },
    { 
      name: 'RESEARCH', 
      path: '/research',
    },
    { 
      name: 'ADMISSIONS', 
      path: '/admissions',
      dropdown: [
        { name: 'How to Apply', path: '/admissions' },
        { name: 'Requirements', path: '/admissions#requirements' },
      ]
    },
    { name: 'NEWS', path: '/blog' },
    { name: 'CONTACT', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0D4A2E] shadow-lg' : 'bg-[#0D4A2E]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src={LOGO_URL} 
              alt="CEPRES International University" 
              className="h-14 w-auto object-contain bg-white rounded-lg p-1"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div 
                key={link.path}
                className="relative"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-[#C5A028]'
                      : 'text-white hover:text-[#C5A028]'
                  }`}
                >
                  {link.name}
                  {link.dropdown && <ChevronDown className="w-4 h-4" />}
                </Link>
                
                {/* Dropdown */}
                {link.dropdown && activeDropdown === link.name && (
                  <div className="absolute top-full left-0 w-56 bg-white shadow-xl py-2 animate-fade-in">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-4 py-3 text-sm text-[#1A1A1A] hover:bg-[#F5F5F5] hover:text-[#0D4A2E] transition-colors duration-200"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Search Icon */}
            <button className="p-3 text-white hover:text-[#C5A028] transition-colors duration-200">
              <Search className="w-5 h-5" />
            </button>

            {/* Student Portal Link */}
            <a
              href="https://cnte.ciu.edu.lr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cnte-link"
              className="ml-2 flex items-center gap-1.5 px-4 py-2 border border-[#C5A028] text-[#C5A028] text-sm font-bold uppercase tracking-wider hover:bg-[#C5A028] hover:text-[#1A1A1A] transition-all duration-300"
            >
              Student Portal
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            {/* Apply Button */}
            <a
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-now-btn"
              className="ml-2 px-6 py-3 bg-[#C5A028] text-[#1A1A1A] font-bold text-sm uppercase tracking-wider hover:bg-[#D4B43D] transition-all duration-300"
            >
              APPLY NOW
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-white"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#082D1C] px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              className={`block px-4 py-3 text-sm font-medium ${
                isActive(link.path)
                  ? 'text-[#C5A028] bg-[#0D4A2E]'
                  : 'text-white hover:text-[#C5A028] hover:bg-[#0D4A2E]'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <a
            href="https://cnte.ciu.edu.lr"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-cnte-link"
            className="flex items-center justify-center gap-2 mt-4 px-4 py-3 border border-[#C5A028] text-[#C5A028] font-bold text-sm uppercase"
          >
            Student Portal
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href="https://cnte.ciu.edu.lr/apply"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-apply-btn"
            className="block mt-2 px-4 py-3 bg-[#C5A028] text-[#1A1A1A] font-bold text-sm text-center uppercase"
          >
            APPLY NOW
          </a>
        </div>
      </div>
    </nav>
  );
};
